import { useEffect, useRef, useCallback, useState } from 'react';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
];

const THROTTLE_MS = 1000;

export interface UseIdleTimerOptions {
  /** Timeout in milliseconds before considered idle (default: 60 min) */
  timeout: number;

  /** Time before timeout to trigger warning (default: 5 min) */
  warningTime: number;

  /** Callback when user becomes idle (timeout reached) */
  onIdle: () => void;

  /** Callback when warning period starts */
  onWarning?: () => void;

  /** Callback when user activity resets the timer */
  onActive?: () => void;

  /** Whether the timer is enabled (default: true) */
  enabled?: boolean;
}

export interface UseIdleTimerResult {
  /** Whether the user is currently idle */
  isIdle: boolean;

  /** Whether the warning dialog should be shown */
  isWarning: boolean;

  /** Time remaining until timeout (in ms) */
  remainingTime: number;

  /** Manually reset the idle timer */
  reset: () => void;

  /** Pause the idle timer */
  pause: () => void;

  /** Resume the idle timer */
  resume: () => void;
}

export function useIdleTimer({
  timeout,
  warningTime,
  onIdle,
  onWarning,
  onActive,
  enabled = true,
}: UseIdleTimerOptions): UseIdleTimerResult {
  const [isIdle, setIsIdle] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(timeout);
  const [isPaused, setIsPaused] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const throttleTimeRef = useRef(0);
  const isWarningRef = useRef(false);
  const isIdleRef = useRef(false);

  // Keep refs in sync with state so the activity handler can read them
  isWarningRef.current = isWarning;
  isIdleRef.current = isIdle;

  const clearTimers = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (warningTimeoutIdRef.current) {
      clearTimeout(warningTimeoutIdRef.current);
      warningTimeoutIdRef.current = null;
    }
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  const startTimers = useCallback(() => {
    if (!enabled || isPaused) return;

    clearTimers();
    lastActivityRef.current = Date.now();
    setIsIdle(false);
    setIsWarning(false);
    setRemainingTime(timeout);

    const warningDelay = timeout - warningTime;
    if (warningDelay > 0 && onWarning) {
      warningTimeoutIdRef.current = setTimeout(() => {
        setIsWarning(true);
        onWarning();
      }, warningDelay);
    }

    timeoutIdRef.current = setTimeout(() => {
      setIsIdle(true);
      setIsWarning(false);
      onIdle();
    }, timeout);

    intervalIdRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, timeout - elapsed);
      setRemainingTime(remaining);
    }, 1000);
  }, [enabled, isPaused, timeout, warningTime, onIdle, onWarning, clearTimers]);

  const reset = useCallback(() => {
    if (!enabled || isPaused) return;

    const now = Date.now();
    if (now - throttleTimeRef.current < THROTTLE_MS) return;
    throttleTimeRef.current = now;

    if (isIdleRef.current || isWarningRef.current) {
      onActive?.();
    }

    startTimers();
  }, [enabled, isPaused, onActive, startTimers]);

  const pause = useCallback(() => {
    setIsPaused(true);
    clearTimers();
  }, [clearTimers]);

  const resume = useCallback(() => {
    setIsPaused(false);
    startTimers();
  }, [startTimers]);

  // Use a ref so the activity handler always calls the latest reset
  const resetRef = useRef(reset);
  resetRef.current = reset;

  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      // Don't reset on DOM activity when warning or idle is active;
      // only the explicit reset() call (e.g. "Stay Signed In") should restart.
      if (isWarningRef.current || isIdleRef.current) return;
      resetRef.current();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    startTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [enabled, startTimers, clearTimers]);

  return {
    isIdle,
    isWarning,
    remainingTime,
    reset,
    pause,
    resume,
  };
}
